import type { ReactNode } from "react";

interface ShowProps<T> {
  /** The condition to evaluate. Renders children when truthy. */
  when: T | undefined | null | false;
  /** Optional content to render when `when` is falsy. */
  fallback?: ReactNode;
  /** Content to render, or a function that receives the truthy `when` value. */
  children: ReactNode | ((item: T) => ReactNode);
}

/**
 * Conditionally renders content based on a truthy condition.
 *
 * Use `Show` for simple conditional rendering instead of ternaries or `&&`.
 * When `when` is truthy, renders children. When falsy, renders fallback (or nothing).
 *
 * **Two patterns:**
 * - **Static children** - when children don't need the `when` value
 * - **Render function** - when you need to use the `when` value in children.
 *   The callback receives the narrowed type (null/undefined excluded), solving
 *   TypeScript errors when passing the value to components expecting non-null props.
 *
 * @example
 * // Static children - content doesn't need the `when` value
 * <Show when={isLoggedIn} fallback={<LoginPrompt />}>
 *   <Dashboard />
 * </Show>
 *
 * @example
 * // Render function - when you need to use the value in children
 * // `user` is typed as User (not User | null) inside the callback
 * <Show when={user}>
 *   {(user) => <span>Hello, {user.name}</span>}
 * </Show>
 *
 * @see {@link Switch} for multiple exclusive conditions (if/else-if/else)
 */
function Show<T>({ when, fallback, children }: ShowProps<T>): ReactNode {
  if (!when) {
    return fallback ?? null;
  }

  if (typeof children === "function") {
    return children(when);
  }

  return children;
}

export default Show;
